import React, { useMemo, useState } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import type { InvestigationThought, InvestigationToolCall } from '../types';
import { ensureSreFontsLoaded } from '../styles/theme';

export interface ExecutionTraceProps {
  toolCalls: InvestigationToolCall[];
  thoughts?: InvestigationThought[];
  isRunning?: boolean;
  className?: string;
}

/** LangGraph investigation stage order (matches graph topology). */
const PHASE_ORDER: Record<string, number> = {
  system: 10,
  init: 20,
  memory: 30,
  memory_lookup: 30,
  kg: 40,
  kg_context: 40,
  planner: 50,
  pulse: 60,
  investigation: 100,
  windows: 100,
  kubernetes: 100,
  k8s: 100,
  subagent: 100,
  metrics: 110,
  log_analysis: 120,
  traces: 130,
  azure: 140,
  aws: 150,
  'sre-agent': 160,
  langgraph: 5,
  unknown: 800,
};

/** Phases that always render after diagnostic skill execution (LangGraph tail). */
const PHASE_TAIL_RANK: Record<string, number> = {
  synthesizer: 1,
  writeup: 2,
  memory_store: 3,
};

function normalizePhaseAgent(agentName: string): string {
  return String(agentName || 'unknown').trim().toLowerCase();
}

function isWriteupToolCall(call: InvestigationToolCall): boolean {
  const name = String(call.name || '').toLowerCase();
  return name === 'llm_writeup' || name.endsWith('.llm_writeup') || name.includes('llm_writeup');
}

function phaseTailRank(agentName: string, calls: InvestigationToolCall[] = []): number {
  const agent = normalizePhaseAgent(agentName);
  if ((PHASE_TAIL_RANK[agent] ?? 0) > 0) {
    return PHASE_TAIL_RANK[agent];
  }
  if (calls.some(isWriteupToolCall)) {
    return PHASE_TAIL_RANK.writeup;
  }
  return 0;
}

function callSequenceKey(call: InvestigationToolCall): number {
  if (call.sequenceNumber != null && !Number.isNaN(call.sequenceNumber)) {
    return call.sequenceNumber;
  }
  return call.startedAt?.getTime() ?? Number.MAX_SAFE_INTEGER;
}

/** Stable phase order — do not use thought seq (LangGraph batches SSE out of order). */
function phaseSortKey(agentName: string, calls: InvestigationToolCall[]): number {
  const agent = normalizePhaseAgent(agentName);
  const tail = phaseTailRank(agentName, calls);
  if (tail > 0) {
    return 900_000 + tail;
  }
  const base = PHASE_ORDER[agent] ?? PHASE_ORDER.unknown;
  if (calls.length > 0) {
    const minCall = Math.min(...calls.map(callSequenceKey));
    return base * 1_000 + Math.min(minCall, 999);
  }
  return base * 1_000;
}

function compareAgentPhases(
  agentA: string,
  callsA: InvestigationToolCall[],
  agentB: string,
  callsB: InvestigationToolCall[],
): number {
  return phaseSortKey(agentA, callsA) - phaseSortKey(agentB, callsB);
}

type TraceItem =
  | { kind: 'tool'; data: InvestigationToolCall }
  | { kind: 'thought'; data: InvestigationThought[] };

function formatDuration(ms?: number): string {
  if (ms == null || Number.isNaN(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function resolveDuration(call: InvestigationToolCall): number | undefined {
  if (call.durationMs != null) return call.durationMs;
  if (call.completedAt && call.startedAt) {
    return Math.max(0, call.completedAt.getTime() - call.startedAt.getTime());
  }
  return undefined;
}

function getToolSummary(call: InvestigationToolCall): string | null {
  const input = call.input;
  if (!input) return null;
  if (typeof input.command === 'string') return input.command.substring(0, 100);
  if (typeof input.query === 'string') return input.query.substring(0, 100);
  if (typeof input.skill === 'string') return `skill: ${input.skill}`;
  if (typeof input.resource_id === 'string') return input.resource_id;
  if (typeof input.description === 'string') return input.description.substring(0, 100);
  const firstVal = Object.values(input).find((v) => typeof v === 'string');
  return firstVal ? String(firstVal).substring(0, 100) : null;
}

function StatusGlyph({ status }: { status: string }) {
  if (status === 'completed' || status === 'success') {
    return <CheckCircleIcon className="h-4 w-4 text-emerald-600" />;
  }
  if (status === 'error') {
    return <XCircleIcon className="h-4 w-4 text-red-500" />;
  }
  if (status === 'running') {
    return <ArrowPathIcon className="h-4 w-4 animate-spin text-primary-600" />;
  }
  return <ClockIcon className="h-4 w-4 text-slate-400" />;
}

function interleaveTrace(
  toolCalls: InvestigationToolCall[],
  thoughts: InvestigationThought[]
): TraceItem[] {
  if (!thoughts.length) {
    return toolCalls.map((tc) => ({ kind: 'tool' as const, data: tc }));
  }
  const items: TraceItem[] = [];
  let thoughtIdx = 0;
  const sorted = [...thoughts].sort((a, b) => a.seq - b.seq);
  for (const tc of toolCalls) {
    const seq = tc.sequenceNumber ?? Number.MAX_SAFE_INTEGER;
    const group: InvestigationThought[] = [];
    while (thoughtIdx < sorted.length && sorted[thoughtIdx].seq <= seq) {
      group.push(sorted[thoughtIdx]);
      thoughtIdx += 1;
    }
    if (group.length > 0) items.push({ kind: 'thought', data: group });
    items.push({ kind: 'tool', data: tc });
  }
  if (thoughtIdx < sorted.length) {
    items.push({ kind: 'thought', data: sorted.slice(thoughtIdx) });
  }
  return items;
}

function ThoughtRow({ thoughts }: { thoughts: InvestigationThought[] }) {
  const [open, setOpen] = useState(false);
  const preview = thoughts[0]?.text?.substring(0, 140) || '';

  return (
    <div className="border-b border-slate-100 bg-secondary-50/40 last:border-b-0">
      <button
        type="button"
        className="flex w-full items-start gap-3 px-3 py-2.5 text-left hover:bg-secondary-50"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="mt-0.5 w-7 shrink-0 text-center font-['IBM_Plex_Mono',ui-monospace,monospace] text-[10px] font-medium text-secondary-700">
          ···
        </span>
        <div className="min-w-0 flex-1">
          <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-secondary-700">
            Reasoning
          </p>
          {open ? (
            <div className="space-y-1.5">
              {thoughts.map((t, i) => (
                <p key={`${t.seq}-${i}`} className="text-xs leading-relaxed text-slate-600">
                  {t.text}
                </p>
              ))}
            </div>
          ) : (
            <p className="truncate text-xs italic text-slate-500">
              {preview}
              {thoughts.length > 1 && (
                <span className="ml-1 not-italic text-secondary-700">
                  +{thoughts.length - 1}
                </span>
              )}
            </p>
          )}
        </div>
        {open ? (
          <ChevronDownIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
        ) : (
          <ChevronRightIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
        )}
      </button>
    </div>
  );
}

function ToolRow({
  call,
  stepNo,
  expanded,
  onToggle,
}: {
  call: InvestigationToolCall;
  stepNo: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const duration = resolveDuration(call);
  const summary = getToolSummary(call);

  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        type="button"
        className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50"
        onClick={onToggle}
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-100 font-['IBM_Plex_Mono',ui-monospace,monospace] text-[11px] font-semibold text-slate-600">
          {String(stepNo).padStart(2, '0')}
        </span>
        <StatusGlyph status={call.status} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="font-['IBM_Plex_Mono',ui-monospace,monospace] text-sm font-medium text-slate-900">
              {call.name}
            </span>
            {call.agentName && (
              <span className="rounded bg-primary-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary-700">
                {call.agentName.replace(/_/g, ' ')}
              </span>
            )}
          </div>
          {summary && (
            <p className="mt-0.5 truncate font-['IBM_Plex_Mono',ui-monospace,monospace] text-[11px] text-slate-400">
              {summary}
            </p>
          )}
        </div>
        <span className="shrink-0 font-['IBM_Plex_Mono',ui-monospace,monospace] text-[11px] text-slate-400">
          {formatDuration(duration)}
        </span>
        {expanded ? (
          <ChevronDownIcon className="h-4 w-4 shrink-0 text-slate-400" />
        ) : (
          <ChevronRightIcon className="h-4 w-4 shrink-0 text-slate-400" />
        )}
      </button>

      {expanded && (
        <div className="sre-wb-fade space-y-3 border-t border-slate-100 bg-slate-50/80 px-3 py-3 pl-[3.25rem]">
          {call.input && Object.keys(call.input).length > 0 && (
            <div>
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                Input
              </p>
              <pre className="max-h-36 overflow-auto rounded-lg border border-slate-200 bg-white p-2.5 font-['IBM_Plex_Mono',ui-monospace,monospace] text-[11px] leading-relaxed text-slate-700">
                {JSON.stringify(call.input, null, 2)}
              </pre>
            </div>
          )}
          {call.output && (
            <div>
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                Output
              </p>
              <pre className="max-h-44 overflow-auto whitespace-pre-wrap rounded-lg border border-slate-200 bg-white p-2.5 font-['IBM_Plex_Mono',ui-monospace,monospace] text-[11px] leading-relaxed text-slate-700">
                {call.output}
              </pre>
            </div>
          )}
          {call.errorMessage && (
            <div>
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-red-500">
                Error
              </p>
              <pre className="whitespace-pre-wrap rounded-lg border border-red-100 bg-red-50 p-2.5 text-[11px] text-red-800">
                {call.errorMessage}
              </pre>
            </div>
          )}
          <div className="flex gap-3 font-['IBM_Plex_Mono',ui-monospace,monospace] text-[10px] text-slate-400">
            {call.sequenceNumber != null && <span>seq {call.sequenceNumber}</span>}
            <span>{call.startedAt.toLocaleTimeString()}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Systematic execution trace — numbered evidence steps (Pulse workbench, not OpenSRE TraceViewer).
 */
const ExecutionTrace: React.FC<ExecutionTraceProps> = ({
  toolCalls,
  thoughts = [],
  isRunning = false,
  className = '',
}) => {
  const [expandedCalls, setExpandedCalls] = useState<Set<string>>(new Set());
  const [collapsedPhases, setCollapsedPhases] = useState<Set<string>>(new Set());

  React.useEffect(() => {
    ensureSreFontsLoaded();
  }, []);

  const scrollRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isRunning) return;
    scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [toolCalls.length, thoughts.length, isRunning]);

  const groupedByAgent = useMemo(() => {
    const groups: Record<string, InvestigationToolCall[]> = {};
    const ensure = (agent: string) => {
      if (!groups[agent]) groups[agent] = [];
    };
    for (const tc of toolCalls) {
      const name = String(tc.name || '').toLowerCase();
      // Methodology load is not a diagnostic skill call — hide it so phases
      // reflect Azure / AKS / Windows scripts that actually ran.
      if (name === 'load_skill' || name.endsWith('.load_skill')) {
        continue;
      }
      const agent = tc.agentName || 'unknown';
      ensure(agent);
      groups[agent].push(tc);
    }
    for (const thought of thoughts) {
      const agent = thought.agent || '';
      if (agent) ensure(agent);
    }
    for (const agent of Object.keys(groups)) {
      groups[agent].sort((a, b) => callSequenceKey(a) - callSequenceKey(b));
    }
    return groups;
  }, [toolCalls, thoughts]);

  const sortedAgents = useMemo(
    () =>
      Object.entries(groupedByAgent).sort(([agentA, callsA], [agentB, callsB]) =>
        compareAgentPhases(agentA, callsA, agentB, callsB)
      ),
    [groupedByAgent]
  );

  const toggleCall = (id: string) => {
    setExpandedCalls((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const togglePhase = (name: string) => {
    setCollapsedPhases((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  if (toolCalls.length === 0 && thoughts.length === 0 && !isRunning) {
    return null;
  }

  let globalStep = 0;

  return (
    <div className={['sre-wb-fade sre-wb-fade-delay-1 space-y-3', className].filter(Boolean).join(' ')}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Squares2X2Icon className="h-4 w-4 text-primary-600" />
          <h4 className="text-sm font-semibold text-slate-800">Execution trace</h4>
          <span className="rounded-md bg-slate-100 px-2 py-0.5 font-['IBM_Plex_Mono',ui-monospace,monospace] text-[11px] font-medium text-slate-600">
            {toolCalls.length} steps
          </span>
        </div>
        {isRunning ? (
          <span className="inline-flex items-center gap-1.5 rounded-md bg-primary-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary-700 ring-1 ring-inset ring-primary-200">
            <span className="sre-wb-dot h-1.5 w-1.5 rounded-full bg-primary-600" />
            In investigation
          </span>
        ) : (
          toolCalls.length > 0 && (
            <span className="inline-flex items-center rounded-md bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-800 ring-1 ring-inset ring-emerald-200">
              Complete
            </span>
          )
        )}
      </div>

      {isRunning && (
        <div className="sre-wb-progress h-0.5 overflow-hidden rounded-full bg-primary-100" />
      )}

      {toolCalls.length === 0 && thoughts.length === 0 && isRunning ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-200 bg-white py-10">
          <ArrowPathIcon className="h-6 w-6 animate-spin text-primary-600" />
          <p className="text-sm font-medium text-slate-600">Gathering evidence…</p>
          <p className="text-xs text-slate-400">Diagnostic steps will appear here</p>
        </div>
      ) : toolCalls.length === 0 && thoughts.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white py-8 text-center">
          <p className="text-sm text-slate-500">No evidence steps recorded for this run.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          {sortedAgents.map(([agentName, calls]) => {
            const agentThoughts = thoughts.filter(
              (t) =>
                normalizePhaseAgent(t.agent || '') === normalizePhaseAgent(agentName) ||
                (!t.agent && Object.keys(groupedByAgent).length === 1)
            );
            const items = interleaveTrace(calls, agentThoughts);
            const collapsed = collapsedPhases.has(agentName);

            return (
              <div key={agentName} className="border-b border-slate-100 last:border-b-0">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 bg-slate-50/90 px-3 py-2 text-left hover:bg-slate-100"
                  onClick={() => togglePhase(agentName)}
                >
                  {collapsed ? (
                    <ChevronRightIcon className="h-4 w-4 text-slate-400" />
                  ) : (
                    <ChevronDownIcon className="h-4 w-4 text-slate-400" />
                  )}
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Phase · {agentName.replace(/_/g, ' ')}
                  </span>
                  <span className="ml-auto font-['IBM_Plex_Mono',ui-monospace,monospace] text-[11px] text-slate-400">
                    {calls.length > 0
                      ? `${calls.length} call${calls.length === 1 ? '' : 's'}`
                      : 'thoughts'}
                  </span>
                </button>
                {!collapsed &&
                  items.map((item, idx) => {
                    if (item.kind === 'thought') {
                      return (
                        <div key={`thought-${agentName}-${idx}`} className="sre-trace-step-enter">
                          <ThoughtRow thoughts={item.data} />
                        </div>
                      );
                    }
                    globalStep += 1;
                    const stepNo = item.data.sequenceNumber ?? globalStep;
                    return (
                      <div key={item.data.id} className="sre-trace-step-enter">
                        <ToolRow
                          call={item.data}
                          stepNo={stepNo}
                          expanded={expandedCalls.has(item.data.id)}
                          onToggle={() => toggleCall(item.data.id)}
                        />
                      </div>
                    );
                  })}
              </div>
            );
          })}
          <div ref={scrollRef} />
        </div>
      )}
    </div>
  );
};

export default ExecutionTrace;
